import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ParseKeys } from 'i18next'
import { publicAPI } from '../services/api'
import type { OrgDetailsSummary, OrgDetailsTimelineWeek, OrgRole } from '../services/api'
import OrgDetailsKpiStrip from '../components/OrgDetailsKpiStrip'
import OrgDetailsTimeline from '../components/OrgDetailsTimeline'
import OrgDetailsAggregatedCarsTable from '../components/OrgDetailsAggregatedCarsTable'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatLeasingselskap } from '../utils/formatLeasingselskap'

type OrgKey = ParseKeys<'organizations'>

const VEHICLE_LABELS: Record<string, OrgKey> = {
  personbil: 'details.vehicle.personbil',
  varebil: 'details.vehicle.varebil',
}
const CLASSIFICATION_LABELS: Record<string, OrgKey> = {
  Leasingselskap: 'details.classification.leasingselskap',
  bilbransje: 'details.classification.bilbransje',
  bilutleieselskap: 'details.classification.bilutleieselskap',
  kommune: 'details.classification.kommune',
  other_organization: 'details.classification.otherOrganization',
}

const vehicleFilterToVarebil = (vf: string): boolean | undefined =>
  vf === 'personbil' ? false : vf === 'varebil' ? true : undefined

function explainFor(classification: string): OrgKey {
  switch (classification) {
    case 'Leasingselskap':
      return 'details.explain.leasingselskap'
    case 'bilbransje':
      return 'details.explain.bilbransje'
    case 'bilutleieselskap':
      return 'details.explain.bilutleieselskap'
    case 'kommune':
      return 'details.explain.kommune'
    default:
      return 'details.explain.default'
  }
}

export default function OrganizationDetails() {
  const { t } = useTranslation('organizations')
  const { orgnr = '' } = useParams<{ orgnr: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  const vehicleFilter = searchParams.get('vehicleFilter') ?? 'alle'
  const classification = searchParams.get('classification') ?? ''
  const minePostnummer = searchParams.get('mine_postnummer') ?? ''
  const varebil = useMemo(() => vehicleFilterToVarebil(vehicleFilter), [vehicleFilter])

  const [summary, setSummary] = useState<OrgDetailsSummary | null>(null)
  const [weeks, setWeeks] = useState<OrgDetailsTimelineWeek[]>([])
  const [loading, setLoading] = useState(true)
  const [errorKind, setErrorKind] = useState<'none' | 'notfound' | 'generic'>('none')

  useEffect(() => {
    if (!orgnr || !from || !to) {
      setErrorKind('generic')
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setErrorKind('none')

    const sharedParams = {
      ...(varebil !== undefined ? { varebil } : {}),
      ...(classification ? { classification } : {}),
      ...(minePostnummer ? { mine_postnummer: minePostnummer } : {}),
    }

    Promise.all([
      publicAPI.getOrgDetailsSummary(orgnr, { from, to, ...sharedParams }),
      publicAPI.getOrgDetailsTimeline(orgnr, sharedParams),
    ])
      .then(([summaryRes, timelineRes]) => {
        if (cancelled) return
        setSummary(summaryRes.data)
        setWeeks(timelineRes.data.weeks)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const status = (err as { response?: { status?: number } })?.response?.status
        setErrorKind(status === 404 ? 'notfound' : 'generic')
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [orgnr, from, to, varebil, classification, minePostnummer])

  const TilbakeButton = (
    <button
      onClick={() => navigate(-1)}
      className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
    >
      <ArrowLeft className="w-4 h-4" />
      {t('details.back')}
    </button>
  )

  if (loading) {
    return (
      <div className="space-y-6">
        {TilbakeButton}
        <LoadingSpinner />
      </div>
    )
  }

  if (errorKind === 'notfound') {
    return (
      <div className="space-y-6">
        {TilbakeButton}
        <div className="rounded-lg border border-gray-200 dark:border-dark-border p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('details.notFoundTitle')}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('details.notFoundText', { orgnr })}
          </p>
        </div>
      </div>
    )
  }

  if (errorKind === 'generic' || !summary) {
    return (
      <div className="space-y-6">
        {TilbakeButton}
        <div className="rounded-lg border border-red-200 dark:border-red-900 p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('details.errorTitle')}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{t('details.errorText')}</p>
        </div>
      </div>
    )
  }

  const availableRoles: OrgRole[] = (
    ['kjopt', 'leaset_inn', 'leaset_ut_naering', 'leaset_ut_privat', 'leaset_ut_udefinert'] as OrgRole[]
  ).filter((r) => summary.selected[r] + summary.rest_of_2025_2026[r] > 0)

  const chips: string[] = []
  if (vehicleFilter !== 'alle' && VEHICLE_LABELS[vehicleFilter]) {
    chips.push(t('details.chips.vehicle', { value: t(VEHICLE_LABELS[vehicleFilter]) }))
  }
  if (classification) {
    chips.push(
      t('details.chips.aktortype', {
        value: CLASSIFICATION_LABELS[classification]
          ? t(CLASSIFICATION_LABELS[classification])
          : classification,
      }),
    )
  }
  if (minePostnummer) {
    chips.push(t('details.chips.postnummer', { value: minePostnummer }))
  }

  return (
    <div className="space-y-6">
      {TilbakeButton}

      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {formatLeasingselskap(summary.org_name)}{' '}
          <span className="text-base font-normal text-gray-500 dark:text-gray-400">
            ({summary.org_number})
          </span>
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t('details.period', { from, to })}
          {chips.length > 0 && <> &middot; {chips.join(' · ')}</>}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('sourceData')}</p>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-900/10 dark:text-blue-200">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>{t(explainFor(summary.classification))}</p>
      </div>

      <OrgDetailsKpiStrip summary={summary} />

      <OrgDetailsTimeline weeks={weeks} selectedFrom={from} selectedTo={to} />

      <OrgDetailsAggregatedCarsTable
        orgnr={orgnr}
        from={from}
        to={to}
        availableRoles={availableRoles}
        varebil={varebil}
        classification={classification || undefined}
        minePostnummer={minePostnummer || undefined}
      />
    </div>
  )
}
